import classNames from 'classnames';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import 'moment/locale/pl';
import ChevronLeftSvg from 'images/v5/icons/chevron-left.svg';
import ChevronRightSvg from 'images/v5/icons/chevron-right.svg';
import Tooltip from 'layout/atoms/Tooltip';
import Button from '../Button/Button';
import styles from './CalendarRange.module.scss';
moment.locale('pl');
function CalendarRange({ startDate = null, endDate = null, onDateChange, initialMonth = null, readOnly = false, singleDate = false, minDate = null, maxDate = null, disabledDates = [], occupiedDates = [], productionDates = [], occupiedDateOrderIds = {}, occupiedDateOrders = {}, legend, onVisibleRangeChange, }) {
    const [currentMonth, setCurrentMonth] = useState(() => initialMonth ? initialMonth.clone().startOf('month') : moment());
    const shouldShowNonWorkingLegend = Boolean((legend === null || legend === void 0 ? void 0 : legend.nonWorking) && disabledDates.length > 0);
    const shouldShowOccupiedLegend = Boolean((legend === null || legend === void 0 ? void 0 : legend.occupied) && occupiedDates.length > 0);
    const shouldShowProductionLegend = Boolean((legend === null || legend === void 0 ? void 0 : legend.production) && productionDates.length > 0);
    const monthStart = currentMonth.clone().startOf('month');
    const monthEnd = currentMonth.clone().endOf('month');
    const gridStart = monthStart.clone().startOf('week');
    const gridEnd = monthEnd.clone().endOf('week');
    useEffect(() => {
        const visibleRangeStart = currentMonth.clone().startOf('month').startOf('week');
        const visibleRangeEnd = currentMonth.clone().endOf('month').endOf('week');
        onVisibleRangeChange === null || onVisibleRangeChange === void 0 ? void 0 : onVisibleRangeChange(visibleRangeStart, visibleRangeEnd);
    }, [currentMonth, onVisibleRangeChange]);
    const days = [];
    const day = gridStart.clone();
    while (day.isSameOrBefore(gridEnd, 'day')) {
        days.push(day.clone());
        day.add(1, 'day');
    }
    const isDateDisabled = (dayToCheck) => {
        if (minDate && dayToCheck.isBefore(minDate, 'day')) {
            return true;
        }
        if (maxDate && dayToCheck.isAfter(maxDate, 'day')) {
            return true;
        }
        if (disabledDates.some((d) => d.isSame(dayToCheck, 'day'))) {
            return true;
        }
        if (occupiedDates.some((d) => d.isSame(dayToCheck, 'day'))) {
            return true;
        }
        return false;
    };
    const handleSelect = (day) => {
        if (readOnly || !onDateChange || isDateDisabled(day)) {
            return;
        }
        if (singleDate) {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(day.clone().startOf('day'), null);
        }
        else if (!startDate || (startDate && endDate)) {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(day.clone().startOf('day'), null);
        }
        else if (day.isBefore(startDate, 'day')) {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(day.clone().startOf('day'), endDate);
        }
        else {
            onDateChange === null || onDateChange === void 0 ? void 0 : onDateChange(startDate, day.clone().startOf('day'));
        }
    };
    const isInRange = (day) => {
        if (startDate && endDate) {
            return day.isAfter(startDate, 'day') && day.isBefore(endDate, 'day');
        }
        return false;
    };
    return (React.createElement("div", { className: styles.wrap },
        React.createElement("div", { className: styles.header },
            React.createElement(Button, { shape: "squared", theme: "ghost", leftIcon: React.createElement(ChevronLeftSvg, null), onClick: () => setCurrentMonth(currentMonth.clone().subtract(1, 'month')) }),
            React.createElement("span", { style: { fontWeight: 'bold' } }, currentMonth.format('MMMM YYYY')),
            React.createElement(Button, { shape: "squared", theme: "ghost", leftIcon: React.createElement(ChevronRightSvg, null), onClick: () => setCurrentMonth(currentMonth.clone().add(1, 'month')) })),
        (shouldShowNonWorkingLegend || shouldShowOccupiedLegend || shouldShowProductionLegend) && (React.createElement("div", { className: styles.legend },
            shouldShowNonWorkingLegend && (React.createElement("div", { className: styles.legendItem },
                React.createElement("span", { className: `${styles.legendBadge} ${styles.nonWorkingBadge}` }),
                React.createElement("span", null, legend === null || legend === void 0 ? void 0 : legend.nonWorking))),
            shouldShowOccupiedLegend && (React.createElement("div", { className: styles.legendItem },
                React.createElement("span", { className: `${styles.legendBadge} ${styles.occupiedBadge}` }),
                React.createElement("span", null, legend === null || legend === void 0 ? void 0 : legend.occupied))),
            shouldShowProductionLegend && (React.createElement("div", { className: styles.legendItem },
                React.createElement("span", { className: `${styles.legendBadge} ${styles.productionBadge}` }),
                React.createElement("span", null, legend === null || legend === void 0 ? void 0 : legend.production))))),
        React.createElement("div", { style: { display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 } }, moment
            .weekdaysMin()
            .slice(1)
            .concat(moment.weekdaysMin().slice(0, 1))
            .map((d) => (React.createElement("div", { key: d, style: { textAlign: 'center', fontWeight: 'bold' } }, d)))),
        React.createElement("div", { className: styles.grid }, days.map((d) => {
            const dayKey = d.format('YYYY-MM-DD');
            const isSelected = (startDate && d.isSame(startDate, 'day')) || (endDate && d.isSame(endDate, 'day'));
            const outsideMonth = d.month() !== currentMonth.month();
            const isNonWorking = disabledDates.some((day) => day.isSame(d, 'day'));
            const isOccupied = occupiedDates.some((day) => day.isSame(d, 'day'));
            const isProduction = productionDates.some((day) => day.isSame(d, 'day'));
            const disabled = isDateDisabled(d);
            const orderIds = occupiedDateOrderIds[dayKey] || [];
            const dayOrders = occupiedDateOrders[dayKey] || [];
            const tooltipOrders = dayOrders.length > 0
                ? dayOrders
                : orderIds.map((orderId) => ({ orderId, specificationNames: [] }));
            const button = (React.createElement("button", { className: classNames(styles.day, {
                    [styles.selected]: isSelected,
                    [styles.inRange]: isInRange(d),
                    [styles.outsideMonth]: outsideMonth,
                    [styles.disabled]: disabled,
                    [styles.readOnly]: readOnly,
                    [styles.nonWorking]: isNonWorking,
                    [styles.occupied]: isOccupied,
                    [styles.production]: isProduction,
                }), type: "button", onClick: () => handleSelect(d), disabled: disabled }, d.date()));
            return (React.createElement("div", { key: dayKey, className: styles.dayCell }, isOccupied && tooltipOrders.length > 0 ? (React.createElement(Tooltip, { theme: "inverse", content: React.createElement("ul", { className: styles.dayTooltipList }, tooltipOrders.map((order) => (React.createElement("li", { key: order.orderId, className: styles.dayTooltipOrderItem },
                    React.createElement("span", { className: styles.dayTooltipOrderId }, order.orderId),
                    (order.specificationNames || []).length > 0 && (React.createElement("ul", { className: styles.dayTooltipSpecList }, (order.specificationNames || []).map((specificationName) => (React.createElement("li", { key: `${order.orderId}-${specificationName}` }, specificationName))))))))), position: "top", delay: 200 },
                React.createElement("div", { className: styles.dayTooltipTrigger }, button))) : (button)));
        }))));
}
export default CalendarRange;
